/**
 * Comfort Bra bundles — in-card expandable variant dropdowns.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { bundleDropdowns, renderBundlePickerPanel, findVariant } from '@theme/comfort-bra-bundle-dropdowns';

/** @returns {Theme['routes'] | null} */
function getThemeRoutes() {
  if (typeof Theme !== 'undefined' && Theme.routes) return Theme.routes;
  return null;
}

class ComfortBraBundles extends HTMLElement {
  connectedCallback() {
    this.data = this.#readProductData();
    this.selectedQty = 3;
    this.selections = [];

    this.updatePrices();
    this.selectBundle(this.querySelector('.cb-bundle.selected') || this.querySelector('.cb-bundle'));

    this.querySelectorAll('.cb-bundle__head').forEach((head) => {
      head.addEventListener('click', () => this.selectBundle(head.closest('.cb-bundle')));
      head.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectBundle(head.closest('.cb-bundle'));
        }
      });
    });

    document.addEventListener('variant:update', () => this.#syncFromMainPicker());
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener('change', () => setTimeout(() => this.#syncFromMainPicker(), 80));
    }

    this.#patchProductForm();
  }

  getBundleState() {
    return {
      selectedQty: this.selectedQty,
      selections: this.#getSelections(this.selectedQty),
    };
  }

  setItemSelection(index, color, size) {
    this.#setSelection(index, color, size);
  }

  #readProductData() {
    const el = document.getElementById('comfort-bra-product-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /** Aggregated variant lines for cart API (variant id → qty). */
  getCartItems() {
    if (!this.data) return null;

    const selections = this.#getSelections(this.selectedQty);
    const counts = new Map();

    for (const sel of selections) {
      const variant = findVariant(this.data, sel.color, sel.size);
      if (!variant) return null;
      counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
    }

    return [...counts.entries()].map(([id, quantity]) => ({
      id: String(id),
      quantity,
    }));
  }

  #needsBundleBatchAdd() {
    return this.selectedQty > 1 && Boolean(this.getCartItems()?.length);
  }

  #patchProductForm() {
    const tryPatch = () => {
      const pf = document.querySelector('product-form-component');
      if (!pf || typeof pf.handleSubmit !== 'function' || pf.dataset.cbBundlePatched === 'true') {
        return pf?.dataset.cbBundlePatched === 'true';
      }

      const original = pf.handleSubmit.bind(pf);

      pf.handleSubmit = (event) => {
        if (!this.data) return original(event);

        this.#syncFormVariantFromFirstSelection();

        if (!this.#needsBundleBatchAdd()) {
          return original(event);
        }

        const items = this.getCartItems();
        if (!items?.length) {
          return original(event);
        }

        event.preventDefault?.();
        this.#executeBundleAddToCart(items, original, event);
      };

      pf.dataset.cbBundlePatched = 'true';
      return true;
    };

    const startPatch = () => {
      if (tryPatch()) return;

      const observer = new MutationObserver(() => {
        if (tryPatch()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 15000);
    };

    if (customElements.get('product-form-component')) {
      startPatch();
    } else {
      customElements.whenDefined('product-form-component').then(startPatch);
    }
  }

  /**
   * @param {Array<{id: string, quantity: number}>} items
   * @param {(event: Event) => void} original
   * @param {Event} event
   */
  async #executeBundleAddToCart(items, original, event) {
    const routes = getThemeRoutes();
    const cartAddUrl = routes?.cart_add_url || '/cart/add.js';
    const cartUrl = routes?.cart_url || '/cart';

    const formComponent = document.querySelector('product-form-component');
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    formComponent?.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.id,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.id),
        quantity: item.quantity,
      })),
      sections: sectionIds.join(','),
    };

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.status) {
        formComponent?.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );

        const ajaxCart = await fetch(`${cartUrl}.js`).then((r) => r.json());
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, itemCount: totalQuantity },
        });
        return original(event);
      }

      const ajaxCart = await fetch(`${cartUrl}.js`).then((r) => r.json());
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          itemCount: totalQuantity,
          sections: result.sections,
          didError: false,
        },
      });

      if (window.Shopify?.actions?.openCart) {
        await window.Shopify.actions.openCart();
      } else {
        document.querySelector('theme-drawer#cart-drawer')?.open?.();
      }
    } catch (error) {
      deferredEventPromise.reject(error);
      formComponent?.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      original(event);
    }
  }

  #syncFormVariantFromFirstSelection() {
    const sel = this.#getSelections(this.selectedQty)[0];
    if (!sel || !this.data) return;

    const variant = findVariant(this.data, sel.color, sel.size);
    if (!variant) return;

    const input = document.querySelector('product-form-component input[name="id"]');
    if (input) input.value = String(variant.id);
  }

  #getMainSelection() {
    const picker = document.querySelector('variant-picker');
    if (!picker || !this.data) return { color: this.data.colors[0]?.name, size: this.data.sizes[0] };

    let color = this.data.colors[0]?.name;
    let size = this.data.sizes[0];

    picker.querySelectorAll('fieldset').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend')?.textContent?.trim();
      const checked = fieldset.querySelector('input:checked');
      if (!checked) return;
      if (legend === this.data.colorOption) color = checked.value;
      if (legend === this.data.sizeOption) size = checked.value;
    });

    return { color, size };
  }

  #syncFromMainPicker() {
    const main = this.#getMainSelection();
    if (!this.selections.length) {
      this.selections = Array.from({ length: this.selectedQty }, () => ({ ...main }));
    } else {
      this.selections[0] = { ...main };
    }
    this.#syncFormVariantFromFirstSelection();
    this.#renderPanels();
    this.#dispatchSelectionChange();
  }

  #syncToMainPicker(color, size) {
    const picker = document.querySelector('variant-picker');
    if (!picker || !this.data) return;

    picker.querySelectorAll('fieldset').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend')?.textContent?.trim();
      let targetValue = null;
      if (legend === this.data.colorOption) targetValue = color;
      if (legend === this.data.sizeOption) targetValue = size;
      if (!targetValue) return;

      const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((r) => r.value === targetValue);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  #getSelections(qty) {
    if (this.selections.length === qty) return this.selections;
    const main = this.#getMainSelection();
    const base = this.selections.length ? [...this.selections] : [];
    while (base.length < qty) base.push({ ...main });
    return base.slice(0, qty);
  }

  selectBundle(card) {
    if (!card) return;
    bundleDropdowns.close();

    this.querySelectorAll('.cb-bundle').forEach((c) => {
      c.classList.remove('selected');
      c.querySelector('.cb-bundle__head')?.setAttribute('aria-pressed', 'false');
    });
    card.classList.add('selected');
    card.querySelector('.cb-bundle__head')?.setAttribute('aria-pressed', 'true');

    const qty = Number(card.dataset.bundleQty) || 1;
    this.selectedQty = qty;
    this.selections = this.#getSelections(qty);

    const input =
      document.querySelector('.product-form input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]');
    if (input) {
      input.value = qty;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.#syncFormVariantFromFirstSelection();
    this.#renderPanels();
    this.#dispatchSelectionChange();
  }

  #renderPanels() {
    if (!this.data) return;

    this.querySelectorAll('[data-cb-bundle-panel]').forEach((panel) => {
      panel.hidden = true;
      panel.innerHTML = '';
    });

    const selected = this.querySelector('.cb-bundle.selected');
    if (!selected) return;

    const panel = selected.querySelector('[data-cb-bundle-panel]');
    if (!panel) return;

    panel.hidden = false;

    renderBundlePickerPanel({
      container: panel,
      data: this.data,
      selections: this.selections,
      controller: bundleDropdowns,
      onSelect: (index, color, size) => this.#setSelection(index, color, size),
    });

    panel.addEventListener('click', (e) => e.stopPropagation());
  }

  #setSelection(index, color, size) {
    const current = this.selections[index] || this.#getMainSelection();
    this.selections[index] = {
      color: color ?? current.color,
      size: size ?? current.size,
    };

    if (index === 0) {
      this.#syncToMainPicker(this.selections[0].color, this.selections[0].size);
      this.#syncFormVariantFromFirstSelection();
    }

    this.#renderPanels();
    this.#dispatchSelectionChange();
  }

  #dispatchSelectionChange() {
    document.dispatchEvent(
      new CustomEvent('comfort-bra:selection-change', {
        detail: {
          bundleQty: this.selectedQty,
          selections: this.#getSelections(this.selectedQty),
          summary: this.#buildSummary(),
        },
      })
    );
  }

  #buildSummary() {
    const qty = this.selectedQty;
    const sel = this.#getSelections(qty)[0] || this.#getMainSelection();
    const color = sel.color || '';
    const size = sel.size || '';
    if (qty > 1) return `${qty} · ${size} · ${color}`;
    return `${size} · ${color}`;
  }

  getPriceFromDom() {
    const sale = document.querySelector('.product-details .price-item--sale');
    const regular = document.querySelector('.product-details .price-item--regular');
    const compare = document.querySelector('.product-details .price-item--compare');

    const parse = (el) => {
      if (!el) return null;
      const raw = el.textContent.replace(/[^\d,.]/g, '').replace(',', '.');
      const val = parseFloat(raw);
      return Number.isFinite(val) ? val : null;
    };

    return {
      price: parse(sale) ?? parse(regular),
      compare: parse(compare),
    };
  }

  updatePrices() {
    const { price, compare } = this.getPriceFromDom();
    if (!price) return;

    const unitCompare = compare ?? price;

    this.querySelectorAll('[data-bundle-price]').forEach((el) => {
      const qty = Number(el.dataset.bundlePrice);
      el.textContent = this.formatMoney(this.bundleTotal(qty, price));
    });

    this.querySelectorAll('[data-bundle-compare]').forEach((el) => {
      const qty = Number(el.dataset.bundleCompare);
      el.textContent = this.formatMoney(unitCompare * qty);
    });
  }

  bundleTotal(qty, unit) {
    if (qty === 3) return unit * 2;
    if (qty === 5) return unit * 3;
    return unit * qty;
  }

  formatMoney(amount) {
    const el = document.querySelector('.product-details .price-item--sale, .product-details .price-item--regular');
    if (!el) return amount.toFixed(0);
    const sample = el.textContent.trim();
    const symbol = sample.replace(/[\d\s,.]/g, '') || '€';
    const formatted = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
    const withSep = sample.includes(',') ? formatted.replace('.', ',') : formatted;
    if (sample.includes(',')) return `${withSep} ${symbol}`.trim();
    if (sample.indexOf(symbol) === 0) return `${symbol}${withSep}`;
    return `${withSep} ${symbol}`.trim();
  }
}

if (!customElements.get('comfort-bra-bundles')) {
  customElements.define('comfort-bra-bundles', ComfortBraBundles);
}
